import { CloudArrowUp, FileText, X } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import { formatCurrency } from '../../../../../../../utils/CurrencyUtils';
import { toast } from 'react-hot-toast';
import { TransactionWithPayments } from '../../../../../../../types/apiTypes';
import { useUploadInvoiceMutation } from '../../hooks/useTransactions';

interface AttachInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionWithPayments | null;
}

const formatBytes = (bytes: number) => {
  if (!bytes && bytes !== 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0,
    n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const truncateFilename = (filename: string, maxLength: number = 25) => {
  if (filename.length <= maxLength) return filename;
  const lastDotIndex = filename.lastIndexOf('.');
  const extension = lastDotIndex !== -1 ? filename.slice(lastDotIndex) : '';
  const nameWithoutExt =
    lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;
  const availableLength = maxLength - extension.length - 3;
  if (availableLength <= 0) return filename.slice(0, maxLength - 3) + '...';
  return nameWithoutExt.slice(0, availableLength) + '...' + extension;
};

export const AttachInvoiceModal = ({
  isOpen,
  onClose,
  transaction,
}: AttachInvoiceModalProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { mutateAsync: uploadInvoice, isPending: isUploading } =
    useUploadInvoiceMutation();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Basic validation logic
    const file = acceptedFiles[0];
    if (!file) return;

    // Check size (10MB limit as per design screenshot)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`"${truncateFilename(file.name)}" exceeds 10MB limit`);
      return;
    }

    setFiles([file]);
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: true,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        const { file, errors } = rejection;
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            toast.error(
              `"${truncateFilename(file.name)}" exceeds the 10MB limit`
            );
          } else {
            toast.error(
              `"${truncateFilename(file.name)}" rejected: ${error.message}`
            );
          }
        });
      });
    },
  });

  const handleBrowse = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    open();
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!files.length || !transaction) return;

    try {
      setUploadProgress(0);
      await uploadInvoice({
        transactionId: transaction.transaction_id,
        file: files[0],
        onProgress: (progress) => setUploadProgress(progress),
      });
      toast.success('Invoice attached successfully');
      setFiles([]);
      onClose();
    } catch (error) {
      toast.error('Failed to upload invoice');
    }
  };

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
      <div className="relative w-full max-w-[520px] rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex h-[56px] items-center justify-between border-b border-bordergray px-6">
          <h2 className="text-[16px] font-[600] text-primary">
            Attach Load Invoice
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-6 p-6">
          <p className="text-[14px] text-graysecondary">
            Attach the load invoice associated with this deposit amount of{' '}
            {formatCurrency(transaction?.amount ?? 0)}.
          </p>

          <div>
            <p className="mb-2 text-[14px] font-[500] text-graysecondary">
              Upload File
            </p>

            {files.length > 0 ? (
              <div className="flex flex-col gap-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-bordergray bg-gray-50 p-3"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText
                        size={24}
                        className="shrink-0 text-graysecondary"
                      />
                      <div className="flex flex-col truncate">
                        <span className="truncate text-sm font-medium text-primary">
                          {file.name}
                        </span>
                        <span className="text-xs text-graysecondary">
                          {formatBytes(file.size)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {isUploading && (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full bg-accent transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={`flex h-[180px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed ${
                  isDragActive
                    ? 'border-accent bg-blue-50'
                    : 'border-bordergray bg-white'
                } transition-colors`}
              >
                <input {...getInputProps()} />
                <CloudArrowUp size={48} className="mb-4 text-gray-400" />
                <p className="text-[14px] text-graysecondary">
                  Drag a file here or{' '}
                  <button
                    onClick={handleBrowse}
                    className="font-medium text-blue-500 hover:underline"
                  >
                    browse
                  </button>{' '}
                  to choose a file or image
                </p>
                <p className="mt-2 text-[12px] text-gray-400">
                  Max 10 MB file is allowed
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 border-t border-bordergray px-6 py-4">
          <NeutralButton onClick={handleClose} addClass="w-full rounded-full">
            Cancel
          </NeutralButton>
          <AccentButton
            onClick={handleUpload}
            disabled={!files.length || isUploading}
            addClass="w-full rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? <ClipLoader size={20} color="#1D213B" /> : 'Upload'}
          </AccentButton>
        </div>
      </div>
    </div>
  );
};

export default AttachInvoiceModal;
